<template>
  <div class="layout__container">
    <div class="layout__width-90 layout__padding-clear">
      <div class="text__colour-teal py-20 text__align-center">
        <h3 class="layout__position-center">Voucher Code Admin</h3>
      </div>

      <vouchers-filters
        :is-loading="isLoading"
        @filters-updated="onFiltersUpdated"
      />

      <vouchers-table
        :vouchers-list="vouchersList"
        :is-loading="isLoading"
        @edit-item="onEditItem"
        @delete-item="onDeleteItem"
        @handle-connections="onHandleConnections"
      />

      <div class="layout__display-flex layout__width-100 justify-end">
        <el-button
          class="button__background-red-border button__base"
          :disabled="isLoading"
          @click.prevent="addVoucher"
        >
          Add new voucher
        </el-button>
      </div>
    </div>

    <voucher-modal
      :is-loading="isLoading"
      :is-edit-voucher-modal-active="isEditVoucherModalActive"
      :editable-item="editableItem"
      :error-message="errorMessage"
      @clear-error-message="errorMessage = ''"
      @save-voucher="onSaveVoucher"
      @on-closed="closeModal"
    />

    <products-modal
      :is-loading="isLoading"
      :products-list="productsList"
      :editable-item="editableItem"
      :is-products-modal-active="isProductsModalActive"
      :error-message="errorMessage"
      @on-closed="closeModal"
      @save-products="onSaveProducts"
    />

    <customers-modal
      :is-loading="isLoading"
      :editable-item="editableItem"
      :is-customers-modal-active="isCustomersModalActive"
      @save-customers="onSaveCustomers"
      @on-closed="closeModal"
    />
  </div>
</template>

<script>
import VouchersFilters from './VouchersFilters.vue';
import VouchersTable from './VouchersTable.vue';
import Voucher from '../../../Models/Voucher';
import VoucherApi from '../../../Api/Voucher';
import VoucherModal from './VoucherModal.vue';
import AdminUserGuard from '../../../mixins/AdminUserGuard.vue';
import CustomersModal from './CustomersModal.vue';
import ProductsModal from './ProductsModal.vue';
import Product from '../../../Models/Product';

export default {
  name: 'VouchersDashboard',

  components: {
    ProductsModal,
    CustomersModal,
    VoucherModal,
    VouchersTable,
    VouchersFilters,
  },

  mixins: [
    AdminUserGuard,
  ],

  data() {
    return {
      vouchersList: [],
      productsList: [],
      isLoading: false,
      isEditVoucherModalActive: false,
      editableItem: null,
      errorMessage: '',
      isCustomersModalActive: false,
      isProductsModalActive: false,
    };
  },

  mounted() {
    this.getProductsList();
    this.getVouchersList();
  },

  methods: {
    async getVouchersList(params = null) {
      this.isLoading = true;

      const response = await VoucherApi.list(params);

      this.vouchersList = response.data.map(voucher => Voucher.make(voucher));

      this.isLoading = false;
    },

    async getProductsList() {
      const response = await VoucherApi.productList();

      this.productsList = response.data.map(product => Product.make(product));
    },

    async onDeleteItem(item) {
      this.isLoading = true;

      let response;

      try {
        response = await VoucherApi.deleteVoucher({ Id: item.Id });
      } catch {
        this.errorMessage = 'Error while deleting item';
        this.isLoading = false;

        return;
      }

      if (response.data.error) {
        this.errorMessage = 'Error while deleting item';
        this.isLoading = false;

        return;
      }

      await this.getVouchersList();

      this.isLoading = false;
    },

    onEditItem(index) {
      this.editableItem = { ...this.vouchersList[index] };
      this.isEditVoucherModalActive = true;
    },

    addVoucher() {
      this.editableItem = null;
      this.isEditVoucherModalActive = true;
    },

    async onSaveVoucher(voucherData) {
      this.isLoading = true;

      let response = null;

      try {
        response = voucherData.Id
          ? await VoucherApi.updateVoucher(voucherData)
          : await VoucherApi.addVoucher(voucherData);
      } catch {
        this.errorMessage = `Error while ${voucherData.Id ? 'editing' : 'creating'} item`;
        this.isLoading = false;

        return;
      }

      if (response.data.error) {
        this.errorMessage = `Error while ${voucherData.Id ? 'editing' : 'creating'} item`;
        this.isLoading = false;

        return;
      }

      this.isEditVoucherModalActive = false;

      await this.getVouchersList();

      this.isLoading = false;
    },

    async onSaveProducts(voucherData) {
      this.isLoading = true;

      let response = null;

      try {
        const data = {
          id: voucherData.Id,
          products: voucherData.Products,
        };

        response = await VoucherApi.updateVoucherProducts(data);
      } catch {
        this.errorMessage = 'Error while updating products';
        this.isLoading = false;

        return;
      }

      if (response.data.error) {
        this.errorMessage = 'Error while updating products';
        this.isLoading = false;

        return;
      }

      this.isProductsModalActive = false;

      await this.getVouchersList();

      this.isLoading = false;
    },

    async onSaveCustomers(voucherData) {
      this.isLoading = true;

      let response = null;

      try {
        const data = {
          id: voucherData.Id,
          customers: voucherData.Customers,
        };

        response = await VoucherApi.updateVoucherCustomers(data);
      } catch {
        this.errorMessage = 'Error while updating customers';
        this.isLoading = false;

        return;
      }

      if (response.data.error) {
        this.errorMessage = 'Error while updating customers';
        this.isLoading = false;

        return;
      }

      this.isCustomersModalActive = false;

      await this.getVouchersList();

      this.isLoading = false;
    },

    onFiltersUpdated(filters) {
      this.getVouchersList(filters);
    },

    onHandleConnections(data) {
      this.editableItem = data.scope.row;

      if (data.field === 'products') {
        this.isProductsModalActive = true;
      }

      if (data.field === 'customers') {
        this.isCustomersModalActive = true;
      }
    },

    closeModal() {
      this.errorMessage = '';
      this.editableItem = null;
      this.isEditVoucherModalActive = false;
      this.isCustomersModalActive = false;
      this.isProductsModalActive = false;
    },
  },
};
</script>
